import { create } from 'zustand';

export type AskUserFieldType = 'text' | 'textarea' | 'select' | 'boolean' | 'number';

export interface AskUserField {
  id: string;
  label: string;
  type: AskUserFieldType;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  options?: Array<{ value: string; label: string }>;
  initialValue?: string | number | boolean;
}

export type AskUserStatus = 'pending' | 'submitted' | 'resolved' | 'cancelled';

export interface AskUserRequest {
  requestId: string;
  sessionId?: string;
  taskId?: string;
  title?: string;
  prompt: string;
  fields?: AskUserField[];
  schema?: Record<string, unknown>; // Added to support generic JSON schema
  metadata?: Record<string, unknown>;
  fromAgent?: string;
  status: AskUserStatus;
  createdAt: number;
  responses?: Record<string, unknown>;
}

interface AskUserStore {
  requests: Map<string, AskUserRequest>;

  addRequest: (request: Omit<AskUserRequest, 'status' | 'createdAt'> & Partial<Pick<AskUserRequest, 'status' | 'createdAt'>>) => void;
  setStatus: (requestId: string, status: AskUserStatus) => void;
  setResponses: (requestId: string, responses: Record<string, unknown>) => void;
  removeRequest: (requestId: string) => void;

  getPendingCount: () => number;
  getRequestsBySession: (sessionId: string) => AskUserRequest[];
}

export const useAskUserStore = create<AskUserStore>((set, get) => ({
  requests: new Map(),

  addRequest: (request) => {
    set((state) => {
      const requests = new Map(state.requests);
      requests.set(request.requestId, {
        ...request,
        status: request.status ?? 'pending',
        createdAt: request.createdAt ?? Date.now(),
      });
      return { requests };
    });
  },

  setStatus: (requestId, status) => {
    set((state) => {
      const requests = new Map(state.requests);
      const existing = requests.get(requestId);
      if (existing) {
        requests.set(requestId, { ...existing, status });
      }
      return { requests };
    });
  },

  setResponses: (requestId, responses) => {
    set((state) => {
      const requests = new Map(state.requests);
      const existing = requests.get(requestId);
      if (existing) {
        requests.set(requestId, { ...existing, responses });
      }
      return { requests };
    });
  },

  removeRequest: (requestId) => {
    set((state) => {
      const requests = new Map(state.requests);
      requests.delete(requestId);
      return { requests };
    });
  },

  getPendingCount: () => {
    const { requests } = get();
    return Array.from(requests.values()).filter((r) => r.status === 'pending').length;
  },

  getRequestsBySession: (sessionId) => {
    const { requests } = get();
    return Array.from(requests.values()).filter((r) => r.sessionId === sessionId);
  },
}));
